import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Clock, Loader2, Send, CheckCircle2, Calendar, AlertCircle } from "lucide-react";

// APScheduler convenção: mon=0..sun=6
const DIAS = [
  { v: 0, label: "Seg" },
  { v: 1, label: "Ter" },
  { v: 2, label: "Qua" },
  { v: 3, label: "Qui" },
  { v: 4, label: "Sex" },
  { v: 5, label: "Sáb" },
  { v: 6, label: "Dom" },
];

const MINUTES_PER_HOUR = 60;
const MINUTES_PER_DAY = 24 * MINUTES_PER_HOUR; // 1440
const GRAJAU_UTC_OFFSET_HOURS = -3; // America/Fortaleza, sem horário de verão

export function utcParaGrajau(h, m) {
  // Converte HH:MM UTC → HH:MM local Grajaú-MA (UTC-3).
  const totalMin = h * MINUTES_PER_HOUR + m
    + GRAJAU_UTC_OFFSET_HOURS * MINUTES_PER_HOUR
    + MINUTES_PER_DAY;
  const local = totalMin % MINUTES_PER_DAY;
  const hh = Math.floor(local / MINUTES_PER_HOUR);
  const mm = local % MINUTES_PER_HOUR;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

export function fmtDataHora(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Fortaleza" }); }
  catch { return iso; }
}

export default function DigestConfigCard({
  cfg, setCfg, loading, saving, testing, onSave, onTest,
}) {
  const toggleDia = (d) => {
    const dias = cfg.dias_semana.includes(d)
      ? cfg.dias_semana.filter((x) => x !== d)
      : [...cfg.dias_semana, d].sort((a, b) => a - b);
    setCfg({ ...cfg, dias_semana: dias });
  };

  const localHora = utcParaGrajau(cfg.hour, cfg.minute);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div>
            <CardTitle className="text-base">Status do envio diário</CardTitle>
            <CardDescription>Habilite para disparar o resumo automático.</CardDescription>
          </div>
          <div className="flex items-center gap-2">
            <Label htmlFor="toggle-enabled" className="text-sm">
              {cfg.enabled ? "Ativado" : "Desativado"}
            </Label>
            <Switch
              id="toggle-enabled"
              checked={cfg.enabled}
              onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })}
              data-testid="toggle-digest-enabled"
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label className="flex items-center gap-1 text-sm">
              <Clock className="w-3.5 h-3.5" /> Hora de envio (UTC)
            </Label>
            <div className="flex items-center gap-2 mt-1">
              <Input
                type="number" min={0} max={23}
                value={cfg.hour}
                onChange={(e) => setCfg({ ...cfg, hour: Number(e.target.value) })}
                className="w-20"
                data-testid="input-hour"
              />
              <span>:</span>
              <Input
                type="number" min={0} max={59}
                value={cfg.minute}
                onChange={(e) => setCfg({ ...cfg, minute: Number(e.target.value) })}
                className="w-20"
                data-testid="input-minute"
              />
              <span className="text-xs text-slate-500">
                UTC · <strong className="text-slate-700">{localHora}</strong> em Grajaú-MA
              </span>
            </div>
          </div>
          <div>
            <Label className="flex items-center gap-1 text-sm">
              <Calendar className="w-3.5 h-3.5" /> Dias da semana
            </Label>
            <div className="flex flex-wrap gap-1.5 mt-1">
              {DIAS.map((d) => {
                const on = cfg.dias_semana.includes(d.v);
                return (
                  <button
                    key={d.v}
                    type="button"
                    onClick={() => toggleDia(d.v)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      on
                        ? "bg-[#083CA6] text-white border-[#083CA6]"
                        : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
                    }`}
                    data-testid={`chk-dia-${d.v}`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 pt-2 border-t">
          <div className="text-xs text-slate-500 flex-1">
            {cfg.next_run && (
              <>Próximo envio: <strong className="text-slate-700">{fmtDataHora(cfg.next_run)}</strong></>
            )}
            {!cfg.next_run && cfg.enabled && (
              <span className="text-amber-700"><AlertCircle className="w-3 h-3 inline" /> Scheduler não registrou — salve novamente.</span>
            )}
            {!cfg.enabled && <span>Nenhum envio agendado.</span>}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={onTest}
              disabled={testing}
              data-testid="btn-digest-testar"
              title="Envia o resumo somente para o seu e-mail"
            >
              {testing
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <Send className="w-4 h-4 mr-2" />}
              Enviar teste agora
            </Button>
            <Button
              onClick={onSave}
              disabled={saving || loading}
              className="bg-[#083CA6] hover:bg-[#062f85]"
              data-testid="btn-digest-salvar"
            >
              {saving
                ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Salvar
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
